/**
 * Data Transformation Utilities
 * Functions to transform API responses to component state
 */

import type {
  PaymentMethod, PaymentMethodKind, PaymentOption, PurchaseDetails
} from '../types';
import type {
  ApiPaymentSource, CombinedPaymentSourcesResponse
} from '../api';
import { isIapAvailable } from './iapSupportUtils';

// ============================================================================
// API Context Type (for narrowing ApiPaymentSource.context)
// ============================================================================

/**
 * Payment method context from API — card/PayPal specific details.
 * Used to narrow the generic `context` field from ApiPaymentSource during mapping.
 */
interface ApiPaymentMethodContext {
  last4?: string;
  type?: string; // Card brand (e.g. "visa", "mastercard")
  exp?: string; // "MM/YYYY" format
  email?: string;
  paypal_billing_agreement_email?: string;
  hasPaypalBillingAgreement?: boolean;
  // Apple Pay session config fields
  [key: string]: unknown;
}

// ============================================================================
// Transformation Functions
// ============================================================================

/**
 * Map API payment source response to PaymentMethod type.
 * Handles snake_case → camelCase conversion for component consumption.
 */
export const mapPaymentMethodFromApi = (apiMethod: ApiPaymentSource): PaymentMethod => {
  const context = (apiMethod.context || {}) as ApiPaymentMethodContext;

  // Parse expiry date from "MM/YYYY" format
  const parseExpiry = (exp?: string) => {
    if (!exp) return { month: undefined, year: undefined };
    const [month, year] = exp.split('/');
    return {
      month: month ? parseInt(month, 10) : undefined,
      year: year ? parseInt(year, 10) : undefined
    };
  };

  const { month: expiryMonth, year: expiryYear } = parseExpiry(context.exp);

  return {
    id: apiMethod.id,
    kind: apiMethod.kind as PaymentMethodKind,
    label: context.type || undefined,
    // IAP has no web gateway — use 'iap' as synthetic gateway name for ChargeIAP routing
    gatewayName: apiMethod.gateway_name || (apiMethod.kind === 'iap' ? 'iap' : undefined),
    last4: context.last4,
    expiryMonth,
    expiryYear,
    fullName: apiMethod.full_name,
    email: context.paypal_billing_agreement_email || context.email,
    isDefault: apiMethod.is_default || false,
    // Error flags from API
    error: apiMethod.error_text || undefined,
    expired: apiMethod.expired || false,
    reauthorizeRequired: apiMethod.reauthorize_required || false,
    // For Apple Merchant configuration status (backend returns camelCase)
    requiresConfiguration: apiMethod.requiresConfiguration || false,
    // PAYG: whether validation API call is required before starting session
    requiresPayAsYouGoValidation: apiMethod.requires_pay_as_you_go_validation,
    // PAYG: whether this method is eligible for pay-as-you-go (from backend)
    eligibleToPayAsYouGo: apiMethod.eligible_to_pay_as_you_go,
    // Store full context for Apple Pay session config, etc.
    context: apiMethod.context,
    // Risk model for fraud detection (device_session_id generation)
    riskModel: apiMethod.risk_model,
    riskModelConfig: apiMethod.risk_model_config,
    // Extra params to merge into process_credits request (present on "add new" options)
    extraParams: apiMethod.extraParams || undefined
  };
};

/**
 * Map API payment options to PaymentOption type
 */
export const mapPaymentOptionsFromApi = (
  available: CombinedPaymentSourcesResponse['payment_sources']['available'] = []
): PaymentOption[] => available.map((opt) => ({
  kind: opt.kind as PaymentMethodKind,
  label: opt.human_kind,
  available: true,
  ...(opt.context ? { context: opt.context } : {})
}));

/**
 * When there are no saved payment methods on PO mobile, pre-select IAP so PurchaseDetailsScreen
 * shows it as the active method. The methods list stays empty — PaymentMethodsListScreen shows
 * "no saved methods" + the Add IAP button.
 */
export const getIapDefaultIfNoMethods = (
  methods: PaymentMethod[],
  currentSelected: PaymentMethod | null,
  purchaseType?: string
): PaymentMethod | null => {
  if (methods.length > 0 || currentSelected) return currentSelected;
  if (!isIapAvailable(purchaseType)) return null;
  return {
    id: 0, kind: 'iap', gatewayName: 'iap', isDefault: true
  };
};

/**
 * Ensure IAP is in the options list when the backend doesn't return it.
 * Only added when isIapAvailable (PO mobile app + purchase type not excluded).
 */
export const ensurePaymentOptionsWithIap = (
  options: PaymentOption[],
  purchaseType?: string
): PaymentOption[] => {
  if (!isIapAvailable(purchaseType)) return options;
  if (options.some((o) => o.kind === 'iap')) return options;
  return [
    ...options,
    { kind: 'iap' as PaymentMethodKind, label: 'In-App Purchase', available: true }
  ];
};

/**
 * Transform API response to PurchaseDetails
 *
 * Note: isMinPurchase is calculated by the wrapper (PurchaseFlowWrapper)
 * based on the mode (additionalFunds vs regular) and passed as a prop.
 *
 * @param response - API response from /my/payment_sources/combined
 * @param price - Display price (final amount to show and pay)
 * @param bonus - Bonus amount
 */
export const transformToPurchaseDetails = (
  response: CombinedPaymentSourcesResponse,
  price: number,
  bonus: number
): PurchaseDetails => {
  const apiFees = response.fees || {};
  const purchaseFees = apiFees.purchase_fees || 0;

  return {
    price,
    bonus,
    availableCredit: response.user.local_available_credit ?? parseFloat(response.user.client_available_credit),
    fees: {
      purchaseFees: apiFees.local_purchase_fees ?? purchaseFees,
      purchaseFeesText: apiFees.local_purchase_fees_text ?? apiFees.purchase_fees_text,
      amountToPay: apiFees.local_amount_to_pay ?? apiFees.amount_to_pay,
      totalAmount: apiFees.local_total_amount ?? apiFees.total_amount,
      creditsRequested: apiFees.local_credits_requested ?? apiFees.credits_requested
    }
  };
};

/**
 * Extract current (default) payment method from API response
 *
 * Logic:
 * - First checks response.current for an actual payment source (not adding)
 * - Falls back to payment_sources.existing to find default
 * - Returns null if no default exists (new user without payment methods)
 *
 * Note: For new users, response.current may contain available options
 * with `adding: true` that aren't actual payment sources.
 */
export const extractCurrentPaymentMethod = (
  response: CombinedPaymentSourcesResponse
): PaymentMethod | null => {
  // First, check if response.current has an actual payment source
  // Items with `adding: true` are options for new payment methods, not existing sources
  if (response.current && response.current.length > 0) {
    const currentItem = response.current[0];
    // Only return if it's an actual payment source, not an "add new" option
    if (!currentItem.adding) {
      // The backend trims the object in `current` — it omits `expired`,
      // `reauthorize_required` and `can_be_used`, keeping only `error_text`.
      // Merge in the full `existing` copy (matched by id) so those flags aren't
      // lost; otherwise the selected method looks healthy and the Pay button
      // stays enabled for an expired card (MBW-2491).
      const existing = response.payment_sources?.existing || [];
      const fullSource = existing.find((source) => source.id === currentItem.id);
      const method = mapPaymentMethodFromApi({ ...currentItem, ...fullSource });
      // response.current represents the default/active payment method,
      // but the API object may not include is_default — set it explicitly.
      method.isDefault = true;
      return method;
    }
  }

  // Fall back to finding default in existing payment sources
  const existing = response.payment_sources?.existing || [];
  const defaultSource = existing.find((source) => source.is_default);

  if (defaultSource) {
    return mapPaymentMethodFromApi(defaultSource);
  }

  // No default payment method (new user)
  return null;
};
